import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/api';
import { isHybridMode } from '../utils/config';

/**
 * Poll for the current user's active match every 15 seconds (hybrid mode only).
 * Also sends presence heartbeats every 30 seconds.
 *
 * @param {string|null} guildId - The guild ID for presence heartbeats
 * @returns {{ activeMatch: object|null, dismissActiveMatch: function }}
 */
export default function useActiveMatch(guildId) {
  const { user, isLoggedIn, getAuthHeaders } = useAuth();
  const [activeMatch, setActiveMatch] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const lastMatchIdRef = useRef(null);

  // Poll for active match
  useEffect(() => {
    if (!isHybridMode || !isLoggedIn) {
      setActiveMatch(null);
      return;
    }
    const checkActive = () => {
      fetch(`${API_URL}/api/web/my-active-match`, { headers: getAuthHeaders() })
        .then(r => {
          // Tillfälligt fel (502 från proxyn): behåll senast kända läge i
          // stället för att dölja banderollen. Nästa lyckade poll (15 s)
          // skriver över det. Utloggad ger 200 + null och nollställer som förut.
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then(data => {
          if (data && data.match_id) {
            // Reset the dismiss/minimize flag whenever the active match changes
            // so a brand new match always surfaces, even if the previous one
            // was minimized.
            if (lastMatchIdRef.current !== data.match_id) {
              lastMatchIdRef.current = data.match_id;
              setDismissed(false);
            }
            setActiveMatch(data);
          } else {
            lastMatchIdRef.current = null;
            setActiveMatch(null);
          }
        })
        .catch(() => {});
    };
    checkActive();
    const interval = setInterval(checkActive, 15000);
    return () => clearInterval(interval);
  }, [isLoggedIn, getAuthHeaders]);

  // Presence heartbeat
  useEffect(() => {
    if (!isHybridMode || !guildId || !isLoggedIn || !user?.discord_id) return;
    const sendPresence = () => {
      fetch(`${API_URL}/api/queue/${guildId}/presence`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ discord_id: user.discord_id }),
      }).catch(() => {});
    };
    sendPresence();
    const interval = setInterval(sendPresence, 30000);
    return () => clearInterval(interval);
  }, [guildId, isLoggedIn, user?.discord_id, getAuthHeaders]);

  return {
    activeMatch,
    activeMatchDismissed: dismissed,
    dismissActiveMatch: () => setDismissed(true),
  };
}
